import { useState } from 'react';
import ScrollReveal from '../../../components/feature/ScrollReveal';

export default function RSVP() {
  const [submitted, setSubmitted] = useState(false);
  const [attending, setAttending] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [textareaLen, setTextareaLen] = useState(0);
  const [venue, setVenue] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const name = (form.elements.namedItem('name') as HTMLInputElement)?.value?.trim();
    const food = (form.elements.namedItem('food') as HTMLTextAreaElement)?.value?.trim();

    if (!name || !attending) return;

    const payload: Record<string, string> = {
      name,
      attending,
    };

    if (attending === 'yes' && venue) {
      payload.venue = venue;
    }

    if (food) {
      payload.food = food;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/rsvp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      setSubmitted(true);
      form.reset();
    } catch (error) {
      console.error('RSVP submit error:', error);
      alert('Не удалось отправить анкету. Попробуйте еще раз.');
    } finally {
      setLoading(false);
    }
  };


  return (
    <section className="relative min-h-screen flex items-center justify-center px-6 py-24"
      style={{ background: '#FAF6F0' }}>

      {/* Corner ornaments */}
      <div className="absolute top-8 left-8 w-14 h-14 border-t border-l border-[#A8323C]/25 pointer-events-none" />
      <div className="absolute top-8 right-8 w-14 h-14 border-t border-r border-[#A8323C]/25 pointer-events-none" />
      <div className="absolute bottom-8 left-8 w-14 h-14 border-b border-l border-[#A8323C]/25 pointer-events-none" />
      <div className="absolute bottom-8 right-8 w-14 h-14 border-b border-r border-[#A8323C]/25 pointer-events-none" />

      <div className="w-full max-w-2xl mx-auto">
        <ScrollReveal direction="fade" threshold={0.05}>
          <div className="text-center mb-12 space-y-3">

            <h2 className="text-5xl md:text-6xl font-serif italic text-[#2A1A14] leading-tight">
              Анкета гостя
            </h2>
            <div className="flex items-center gap-3 justify-center pt-1">
              <div className="h-px w-12 bg-[#A8323C]/50" />
              <span className="text-[#A8323C] text-sm">✦</span>
              <div className="h-px w-12 bg-[#A8323C]/50" />
            </div>
            <p className="text-base text-[#6B5040] font-light leading-relaxed max-w-md mx-auto pt-1">
              Пожалуйста, заполните анкету до{' '}
              <strong className="font-medium text-[#2A1A14]">1 июля 2026</strong>,
              чтобы мы успели всё подготовить
            </p>
          </div>
        </ScrollReveal>

        {submitted ? (
          <ScrollReveal direction="up" threshold={0.05}>
            <div className="text-center py-16 space-y-6">
              <div className="w-16 h-16 flex items-center justify-center mx-auto border border-[#A8323C]/40 text-[#A8323C]">
                <i className="ri-heart-line text-2xl"></i>
              </div>
              <h3 className="text-3xl font-serif italic text-[#2A1A14]">Спасибо!</h3>
              <p className="text-[#6B5040] text-base leading-relaxed max-w-sm mx-auto">
                {attending === 'yes'
                  ? 'Мы очень рады, что вы будете с нами в этот день. До встречи!'
                  : 'Жаль, что вас не будет рядом.'}
              </p>
            </div>
          </ScrollReveal>
        ) : (
          <ScrollReveal direction="up" delay={200} threshold={0.05}>
            {/* Parchment-style form container */}
            <div className="relative border border-[#A8323C]/30 p-6 sm:p-10"
              style={{
                background: '#E8DDD0',
                outline: '1px solid rgba(168,50,60,0.1)',
                outlineOffset: '6px',
              }}>
              {/* Corner marks */}
              {['top-2 left-2', 'top-2 right-2', 'bottom-2 left-2', 'bottom-2 right-2'].map((pos, i) => (
                <span key={i} className={`absolute ${pos} text-[#A8323C] opacity-30 text-base font-serif select-none`}>✦</span>
              ))}

              <form onSubmit={handleSubmit} className="space-y-8">

                {/* Attending */}
                <div>
                  <label className="block text-xs tracking-[0.3em] uppercase font-mono text-[#8B6B5A] mb-4 border-b border-[#A8323C]/15 pb-2">
                    Придёте на свадьбу?
                  </label>
                  <div className="flex flex-col sm:flex-row gap-3">
                    {[
                      { value: 'yes', label: 'Да, обязательно буду!' },
                      { value: 'no', label: 'К сожалению, не смогу' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setAttending(opt.value)}
                        className={`flex-1 py-3 px-5 border text-sm transition-all duration-200 cursor-pointer whitespace-nowrap font-mono tracking-wide ${
                          attending === opt.value
                            ? 'bg-[#2A1A14] text-[#F5F0E8] border-[#2A1A14]'
                            : 'bg-[#F5EFE6] text-[#6B5040] border-[#A8323C]/35 hover:border-[#A8323C]/70'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-xs tracking-[0.3em] uppercase font-mono text-[#8B6B5A] mb-3">
                    Ваше имя <span className="text-[#A8323C]">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Иван Иванов"
                    className="w-full bg-[#F5EFE6] border border-[#A8323C]/30 px-5 py-3 text-sm text-[#2A1A14] placeholder-[#BFB6AA]
                               focus:outline-none focus:border-[#A8323C]/70 transition-colors font-sans"
                  />
                </div>

                {/* Venue — only if attending */}
                {attending === 'yes' && (
                  <div>
                    <label className="block text-xs tracking-[0.3em] uppercase font-mono text-[#8B6B5A] mb-4 border-b border-[#A8323C]/15 pb-2">
                      Где вас ждать?
                    </label>
                    <div className="flex flex-col gap-3">
                      {[
                        { value: 'zags', label: 'С самого начала — в ЗАГСе', sub: 'Церемония регистрации и ресторан' },
                        { value: 'restaurant', label: 'Сразу в ресторане', sub: 'Только праздничный ужин' },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setVenue(opt.value)}
                          className={`w-full py-3 px-5 border text-left transition-all duration-200 cursor-pointer ${
                            venue === opt.value
                              ? 'bg-[#2A1A14] text-[#F5F0E8] border-[#2A1A14]'
                              : 'bg-[#F5EFE6] text-[#6B5040] border-[#A8323C]/35 hover:border-[#A8323C]/70'
                          }`}
                        >
                          <span className="block text-sm font-sans">{opt.label}</span>
                          <span className={`block text-xs mt-0.5 font-mono ${venue === opt.value ? 'text-[#F5F0E8]/60' : 'text-[#A8907A]'}`}>{opt.sub}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Food preferences */}
                {attending === 'yes' && (
                  <div>
                    <label className="block text-xs tracking-[0.3em] uppercase font-mono text-[#8B6B5A] mb-3">
                      Пищевые предпочтения / аллергии
                    </label>
                    <textarea
                      name="food"
                      maxLength={500}
                      rows={3}
                      placeholder="Вегетарианское меню, без глютена, аллергия на орехи..."
                      onChange={(e) => setTextareaLen(e.target.value.length)}
                      className="w-full bg-[#F5EFE6] border border-[#A8323C]/30 px-5 py-3 text-sm text-[#2A1A14]
                                 placeholder-[#BFB6AA] focus:outline-none focus:border-[#A8323C]/70 transition-colors resize-none font-sans"
                    />
                    <p className="text-xs text-[#A8907A] text-right mt-1 font-mono">{textareaLen}/500</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!attending || loading}
                  className="w-full py-4 bg-[#2A1A14] text-[#F5F0E8] text-xs tracking-[0.35em] uppercase font-mono
                             transition-all duration-200 hover:bg-[#A8323C] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap
                             border border-[#2A1A14] hover:border-[#A8323C]"
                >
                  {loading ? 'Отправляем...' : 'Отправить анкету'}
                </button>

                <p className="text-center text-xs text-[#A8907A] font-mono">
                  * — обязательное поле
                </p>
              </form>
            </div>
          </ScrollReveal>
        )}
      </div>
    </section>
  );
}
